/**
 * The `/forgot-password` page's confirmation, over the still-mounted card
 * (DOP-275).
 *
 * A modal rather than a second page state because the thing behind it is what
 * the user comes back to: the address they typed. Closing this returns them to
 * a filled-in field, not to a blank form they have to retype.
 *
 * Presentation only. The cooldown, the resend call and the error text belong
 * to the page, which owns the request — this renders what it is told and
 * reports the two things a user can ask for.
 */

import React from "react";
import { Link } from "react-router-dom";
import ForgotPasswordSent from "./ForgotPasswordSent";
import ModalDialog from "./ModalDialog";

export interface ForgotPasswordSentDialogProps {
	/** The address the instructions went to. */
	email: string;
	/** Seconds left before a resend is allowed; 0 means it is allowed now. */
	cooldownSeconds: number;
	/** A resend is in flight. */
	sending: boolean;
	/** A failed resend, shown in the dialog rather than behind it. */
	error: string;
	onResend: () => void;
	/** Escape and `Use a different email` both land here. */
	onClose: () => void;
}

const TITLE_ID = "forgot-password-sent-title";

const ForgotPasswordSentDialog: React.FC<ForgotPasswordSentDialogProps> = ({
	email,
	cooldownSeconds,
	sending,
	error,
	onResend,
	onClose,
}) => (
	<ModalDialog titleId={TITLE_ID} onClose={onClose}>
		<ForgotPasswordSent
			email={email}
			titleId={TITLE_ID}
			cooldownSeconds={cooldownSeconds}
			sending={sending}
			error={error}
			onResend={onResend}
			footer={
				<>
					<button
						type="button"
						onClick={onClose}
						className="font-medium text-primary hover:opacity-80"
					>
						Use a different email
					</button>
					<Link
						to="/login"
						className="font-medium text-primary hover:opacity-80"
					>
						Back to login
					</Link>
				</>
			}
		/>
	</ModalDialog>
);

export default ForgotPasswordSentDialog;
